import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import { User } from "firebase/auth";
import { deleteAccount } from "../../api/auth";

type DangerZoneSettingsProps = {
  currentUser: User | null;
};

const DangerZoneSettings = ({ currentUser }: DangerZoneSettingsProps) => {
  void currentUser;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const isExactDeleteConfirmation = deleteInput === "delete";

  const getDeleteErrorMessage = (error: unknown) => {
    const status =
      typeof error === "object" &&
      error !== null &&
      "response" in error &&
      typeof (error as { response?: { status?: number } }).response?.status === "number"
        ? (error as { response: { status: number } }).response.status
        : null;

    if (status === 401) {
      return "Your session expired. Please sign in again.";
    }
    if (status === 400) {
      return "Confirmation did not match. Type 'delete' exactly to continue.";
    }
    if (status === 403) {
      return "You are not allowed to delete this account.";
    }
    if (status === 409) {
      return "This tenant is not eligible for self-service deletion.";
    }
    if (status === 500) {
      return "Deletion failed. Please try again or contact support.";
    }
    return "Something went wrong. Please try again.";
  };

  const handleDeleteAccount = async () => {
    setDeleteError(null);

    if (!isExactDeleteConfirmation) {
      setDeleteError("You must type 'delete' to confirm.");
      return;
    }

    try {
      setIsDeleting(true);
      await deleteAccount({ confirmation: "delete" });

      // Redirect to login after deletion
      window.location.href = "/login";
    } catch (error) {
      console.error("❌ Failed to delete account:", error);
      setDeleteError(getDeleteErrorMessage(error));
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Danger Zone
      </Typography>

      <Button
        variant="outlined"
        color="error"
        onClick={() => setConfirmOpen(true)}
        disabled={isDeleting}
      >
        Delete Account
      </Button>

      <Dialog open={confirmOpen} onClose={() => !isDeleting && setConfirmOpen(false)}>
        <DialogTitle>Confirm Account Deletion</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will permanently delete your business and all related data. Type{" "}
            <strong>delete</strong> to confirm.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            placeholder="Type 'delete'"
            value={deleteInput}
            onChange={(e) => setDeleteInput(e.target.value)}
            error={!!deleteError}
            helperText={deleteError || (isDeleting ? "Deleting your account..." : undefined)}
            disabled={isDeleting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={!isExactDeleteConfirmation || isDeleting}
            startIcon={isDeleting ? <CircularProgress color="inherit" size={16} /> : undefined}
          >
            {isDeleting ? "Deleting..." : "Confirm Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DangerZoneSettings;
